import { OUTER, SECTOR, POSTCODE } from "../consts/layers";
import {
  ADD,
  REPLACE,
  REMOVE,
  ADD_REDIRECT,
  REMOVE_REDIRECT,
} from "../consts/map";
import {
  DEFAULT_TIMEBAND,
  SET_TO_NEXT,
  SET_TO_ABOVE,
  LEAVE_UNALLOCATED,
  REPLACE_THIS_LEVEL,
  REPLACE_ALL,
  REPLACE_UNALLOCATED,
} from "../consts/strings";
import { getTimestampFromHHMM, isHHMMFormat } from "./date";
import { groupSectors, groupPostcodes } from "./lookUpTables";
import { Postcode } from "./postcode";
import { getRedirectToRouteCode } from "./routeConfig";
import {
  getRedirectionRouteCode,
  isNonRedirectionRouteCode,
  isRedirectionRouteCode,
} from "./routes";

export const getRouteEditOptions = ({
  postcodeKey,
  routeConfig,
  redirectConfig,
  planType,
  selectedTimeBand,
  routeEditCode,
  routesByOuter,
  lookupTables,
}) => {
  const {
    impactRouteCodes, // route codes of any route with any allocation on any level within edited outer, edited route itself or redirection route for a plan type/timeband
    nestedRouteCodes, // route codes allocated to any areas nested within edited area
    nestedLabelRouteCodes, // label route codes allocated to any areas nested within edited area
    outer, // postcodeKey outer
    sector, // postcodeKey sector
    sectors, // all sectors within the postcodeKey outer
    postcodes, // all postcodes within the postcodeKey outer
    postcode, // postcode object for the postcodeKey
    level, // postcodeKey level
    outerConfig, // config for the postcodeKey outer (if available)
    sectorConfig, // config for the postcodeKey outer (if available)
    mapRouteCode, // routeCode visible on the map
    isRedirect, // true if postcodeKey is allocated to redirection route
    redirectionRouteCode, // redirectionRouteCode route code of the special redirect route for the wave
    redirectToRouteCode, // redirectToRouteCode first available non redirect route on any of the future waves for the postcode
    redirectToRouteCodes, // if level is sector then redirectToRouteCode for sector and postcodes within that sector, otherwise just redirect for postcode
    inheritedRouteCode, // route inherited from above
    inherited, // true if map route code is inherited from above
    inheritedRedirect, // true if level POSTCODE and SECTOR above is redirect route
  } = getPostcodeConfigs({
    postcodeKey,
    routeConfig,
    redirectConfig,
    routesByOuter,
    planType,
    selectedTimeBand,
    routeEditCode,
    lookupTables,
  });

  let options = [];
  // if area has redirect route above
  if (inheritedRedirect)
    options = getInheritedRedirectEditActions({
      routeEditCode,
      mapRouteCode,
      redirectToRouteCode,
      outerConfig,
    });
  // If routeCode on the map is inherited
  else if (inherited)
    options = getInheritanceEditActions({
      routeEditCode,
      redirectionRouteCode,
      redirectToRouteCode,
      redirectToRouteCodes,
      outerConfig,
      sectorConfig,
    });
  // if route code on the map is from redirect
  else if (isRedirect)
    options = getRedirectEditActions({
      routeEditCode,
      redirectionRouteCode,
      inheritedRouteCode,
    });
  // if route code on the map or route code from the config above is equal to edited route code
  else if ([mapRouteCode, inheritedRouteCode].includes(routeEditCode))
    options = getRemovalEditActions({
      redirectionRouteCode,
      redirectToRouteCode,
      redirectToRouteCodes,
      mapRouteCode,
      inheritedRouteCode,
      routeEditCode,
      outerConfig,
      sectorConfig,
    });
  // otherwise add
  else
    options = getAddEditActions({
      mapRouteCode,
      routeEditCode,
    });

  const getDataNormalization = (option) => ({
    consolidation: getActionConsolidation({
      ...option,
      outerConfig,
      sectorConfig,
      postcode,
    }),
    rangeRemovals: getActionRangeRemovals({
      ...option,
      postcode,
      outerConfig,
      redirectionRouteCode,
      lookupTables,
    }),
  });

  options = options
    .map((option) => ({
      ...option,
      planType,
      level,
      timeBand: selectedTimeBand,
      outer,
      sector,
      sectors,
      postcodes,
      postcode: postcode.getPostcode(level),
      routeEditCode,
      impactRouteCodes,
    }))
    .map((option) => ({
      ...option,
      ...getDataNormalization(option),
    }));
  return getActionsUpsertVersion({
    routeEditCode,
    options,
    nestedRouteCodes,
    nestedLabelRouteCodes,
  });
};

const getPostcodeConfigs = ({
  postcodeKey,
  routeConfig,
  redirectConfig,
  routesByOuter,
  planType,
  selectedTimeBand,
  routeEditCode,
  lookupTables,
}) => {
  const postcode = new Postcode(postcodeKey);
  const level = postcode.getLevel();
  const outer = postcode.toOuter();
  const sector = postcode.toSector();
  const sectors = groupSectors(lookupTables.SECTOR)[outer];
  const postcodes = sectors.reduce((acc, sector) => {
    acc[sector] = groupPostcodes(lookupTables.POSTCODE)[sector];
    return acc;
  }, {});
  if (postcode.getLevel() !== level)
    throw new Error("Invalid postcode level for the layer");

  let timeBand = DEFAULT_TIMEBAND;
  if (isHHMMFormat(selectedTimeBand))
    timeBand = getTimestampFromHHMM(selectedTimeBand);

  const waveConfig = routeConfig.find(
    ({ plannedReadyTimeBand }) => plannedReadyTimeBand === timeBand
  );
  const outerConfig = waveConfig?.outer?.find(
    (o) => o.outer === outer && o.routeType === planType
  );
  const sectorConfig =
    level !== OUTER && outerConfig?.sector?.find((s) => s.sector === sector);
  const postcodeConfig =
    level === POSTCODE &&
    outerConfig?.postcode?.find((p) => p.postcode === postcode.toPostcode());

  const isRedirect =
    (level === SECTOR && isRedirectionRouteCode(sectorConfig?.routeCode)) ||
    (level === POSTCODE && isRedirectionRouteCode(postcodeConfig?.routeCode));

  const redirectionRouteCode = getRedirectionRouteCode(
    planType,
    selectedTimeBand
  );
  const redirectToRouteCodes = getRedirectToRouteCodes({
    routeConfig,
    planType,
    postcode,
    selectedTimeBand,
    postcodes,
  });

  const inheritedRouteCode =
    level !== OUTER &&
    ((level === POSTCODE &&
      isNonRedirectionRouteCode(sectorConfig?.routeCode) &&
      sectorConfig?.routeCode) ||
      outerConfig?.routeCode);

  let mapRouteCode =
    postcodeConfig?.routeCode ||
    sectorConfig?.routeCode ||
    outerConfig?.routeCode;
  if (!mapRouteCode || isRedirectionRouteCode(mapRouteCode))
    mapRouteCode = getRedirectToRouteCode({
      routeConfig,
      planType,
      selectedTimeBand,
      postcode: postcodeKey,
      minLevel: level,
    });

  const { inherited, inheritedRedirect } = isInheritedAllocation({
    routeEditCode,
    mapRouteCode,
    postcode,
    outerConfig,
    sectorConfig,
  });

  const impactRouteCodes = getImpactRouteCodes({
    routeEditCode,
    routesByOuter,
    planType,
    postcode,
    redirectionRouteCode,
  });

  const { nestedRouteCodes, nestedLabelRouteCodes } = getNestedRouteCodes({
    routeEditCode,
    outerConfig,
    redirectConfig,
    postcode,
  });

  return {
    impactRouteCodes,
    nestedRouteCodes,
    nestedLabelRouteCodes,
    outer,
    sector,
    postcode,
    sectors,
    postcodes,
    level,
    outerConfig,
    sectorConfig,
    mapRouteCode,
    isRedirect,
    redirectionRouteCode,
    redirectToRouteCode: redirectToRouteCodes[0],
    redirectToRouteCodes: redirectToRouteCodes,
    inheritedRouteCode,
    inherited,
    inheritedRedirect,
  };
};

const isInheritedAllocation = ({
  routeEditCode,
  mapRouteCode,
  postcode,
  outerConfig,
  sectorConfig,
}) => {
  const level = postcode.getLevel();
  switch (level) {
    // Is not inherited if level is OUTER as outer does not inherits
    case OUTER:
      return {
        inherited: false,
        inheritedRedirect: false,
      };

    // Is inherited if level is SECTOR and has OUTER allocation to edit route
    case SECTOR:
      return {
        inherited: [routeEditCode, mapRouteCode].every(
          (routeCode) => routeCode === outerConfig?.routeCode
        ),
        inheritedRedirect: false,
      };

    // Is inherited if level is POSTCODE and config above is allocated to edit route
    // and doesn't have POSTCODE allocation to specifically different then edit route
    case POSTCODE:
      const routeCode = sectorConfig?.routeCode || outerConfig?.routeCode;
      return {
        inherited:
          routeCode === routeEditCode &&
          !outerConfig?.postcode?.find(
            (p) =>
              p.postcode === postcode.toPostcode() &&
              p.routeCode &&
              p.routeCode !== routeEditCode
          ),
        // if level is POSTCODE and sector config is redirect
        inheritedRedirect: isRedirectionRouteCode(sectorConfig?.routeCode),
      };
    default:
      throw new Error(`Invalid postcode level ${level}`);
  }
};

const getImpactRouteCodes = ({
  routeEditCode,
  routesByOuter,
  planType,
  postcode,
  redirectionRouteCode,
}) => {
  const impactRouteCodes = new Set([
    routeEditCode,
    ...(routesByOuter[planType][postcode.toOuter()] || []),
    redirectionRouteCode,
  ]);
  return Array.from(impactRouteCodes);
};

const getNestedRouteCodes = ({
  routeEditCode,
  outerConfig,
  redirectConfig,
  postcode,
}) => {
  const level = postcode.getLevel();
  const nestedRouteCodes = new Set();
  const nestedLabelRouteCodes = new Set();
  if (level === OUTER) {
    outerConfig?.sector?.forEach((s) => {
      nestedRouteCodes.add(s.routeCode);
      if (!isRedirectionRouteCode(s.routeCode))
        return nestedLabelRouteCodes.add(s.routeCode);

      Object.values(redirectConfig[s.routeCode][s.sector]).forEach(
        (redirectToRouteCode) => nestedLabelRouteCodes.add(redirectToRouteCode)
      );
    });
    outerConfig?.postcode?.forEach((p) => {
      nestedRouteCodes.add(p.routeCode);
      if (!isRedirectionRouteCode(p.routeCode))
        return nestedLabelRouteCodes.add(p.routeCode);
      const {
        [p.postcode]: postcodeRedirectToRouteCode,
        routeCode: sectorRedirectToRouteCode,
      } = redirectConfig[p.routeCode][Postcode.toSector(p.postcode)];
      nestedLabelRouteCodes.add(
        postcodeRedirectToRouteCode === undefined
          ? sectorRedirectToRouteCode
          : postcodeRedirectToRouteCode
      );
    });
  } else if (level === SECTOR) {
    outerConfig?.postcode?.forEach((p) => {
      const sector = postcode.toSector();
      if (Postcode.toSector(p.postcode) !== sector) return;

      nestedRouteCodes.add(p.routeCode);
      if (!isRedirectionRouteCode(p.routeCode))
        return nestedLabelRouteCodes.add(p.routeCode);
      const {
        [p.postcode]: postcodeRedirectToRouteCode,
        routeCode: sectorRedirectToRouteCode,
      } = redirectConfig[p.routeCode][sector];
      nestedLabelRouteCodes.add(
        postcodeRedirectToRouteCode === undefined
          ? sectorRedirectToRouteCode
          : postcodeRedirectToRouteCode
      );
    });
  }
  return {
    nestedRouteCodes: Array.from(nestedRouteCodes).filter(
      (routeCode) => routeCode !== routeEditCode
    ),
    nestedLabelRouteCodes: Array.from(nestedLabelRouteCodes).filter(
      (routeCode) => routeCode !== routeEditCode
    ),
  };
};

const getRedirectToRouteCodes = ({
  routeConfig,
  planType,
  postcode,
  postcodes,
  selectedTimeBand,
}) => {
  const level = postcode.getLevel();
  let redirectToRouteCodes = [null];
  if (level !== OUTER)
    redirectToRouteCodes = new Set([
      getRedirectToRouteCode({
        routeConfig,
        planType,
        selectedTimeBand,
        postcode: postcode.getPostcode(level),
      }),
    ]);
  if (level === SECTOR)
    postcodes[postcode.toSector()].forEach((postcode) =>
      redirectToRouteCodes.add(
        getRedirectToRouteCode({
          routeConfig,
          planType,
          selectedTimeBand,
          postcode,
        })
      )
    );
  return Array.from(redirectToRouteCodes);
};

const getInheritedRedirectEditActions = ({
  routeEditCode,
  mapRouteCode,
  redirectToRouteCode,
  outerConfig,
}) => {
  if (mapRouteCode !== routeEditCode)
    return [
      {
        type: ADD,
        affectedRouteCode: mapRouteCode,
        routeCode: routeEditCode,
      },
    ];

  const options = [
    {
      message: redirectToRouteCode ? SET_TO_NEXT : LEAVE_UNALLOCATED,
      labelRouteCodes: [redirectToRouteCode],
      type: REMOVE,
      affectedRouteCode: routeEditCode,
      routeCode: routeEditCode,
    },
  ];

  if (outerConfig.routeCode !== routeEditCode)
    options.push({
      message: SET_TO_ABOVE,
      labelRouteCodes: [outerConfig.routeCode],
      type: REPLACE,
      affectedRouteCode: outerConfig.routeCode,
      routeCode: outerConfig.routeCode,
    });

  return options;
};

const getInheritanceEditActions = ({
  routeEditCode,
  redirectionRouteCode,
  redirectToRouteCode,
  redirectToRouteCodes,
  outerConfig,
  sectorConfig,
}) => {
  const options = [];
  options.push({
    message: redirectToRouteCode ? SET_TO_NEXT : LEAVE_UNALLOCATED,
    labelRouteCodes: redirectToRouteCodes,
    type: ADD_REDIRECT,
    affectedRouteCode: routeEditCode,
    routeCode: redirectionRouteCode,
  });

  if (
    outerConfig?.routeCode &&
    sectorConfig?.routeCode &&
    sectorConfig.routeCode === routeEditCode
  )
    options.push({
      message: SET_TO_ABOVE,
      labelRouteCodes: [outerConfig.routeCode],
      type: REPLACE,
      affectedRouteCode: outerConfig.routeCode,
      routeCode: outerConfig.routeCode,
    });
  return options;
};

const getRedirectEditActions = ({
  routeEditCode,
  redirectionRouteCode,
  inheritedRouteCode,
}) => [
  inheritedRouteCode === routeEditCode
    ? {
        type: REMOVE_REDIRECT,
        affectedRouteCode: redirectionRouteCode,
        routeCode: redirectionRouteCode,
      }
    : {
        type: ADD,
        affectedRouteCode: redirectionRouteCode,
        routeCode: routeEditCode,
      },
];

const getRemovalEditActions = ({
  redirectionRouteCode,
  redirectToRouteCode,
  redirectToRouteCodes,
  mapRouteCode,
  inheritedRouteCode,
  routeEditCode,
}) => {
  const options = [];
  if (mapRouteCode === routeEditCode) {
    if (inheritedRouteCode)
      options.push({
        message: redirectToRouteCode ? SET_TO_NEXT : LEAVE_UNALLOCATED,
        labelRouteCodes: redirectToRouteCodes,
        type: ADD_REDIRECT,
        affectedRouteCode: routeEditCode,
        routeCode: redirectionRouteCode,
      });

    options.push({
      message: inheritedRouteCode ? SET_TO_ABOVE : LEAVE_UNALLOCATED,
      labelRouteCodes: [inheritedRouteCode || null],
      type: REMOVE,
      affectedRouteCode: routeEditCode,
      routeCode: routeEditCode,
    });
  } else
    options.push({
      type: REMOVE,
      affectedRouteCode: mapRouteCode,
      routeCode: mapRouteCode,
    });

  return options;
};

const getAddEditActions = ({ mapRouteCode, routeEditCode }) => [
  {
    type: ADD,
    affectedRouteCode: mapRouteCode,
    routeCode: routeEditCode,
  },
];

const getActionConsolidation = ({
  outerConfig,
  sectorConfig,
  routeCode,
  routeEditCode,
  postcode,
  sectors,
  postcodes,
  type,
}) => {
  const level = postcode.getLevel();
  if (level === OUTER) return {};
  const sector = postcode.toSector();
  postcode = postcode.getPostcode(level);

  let toOuter = false;
  let toSector = false;
  const sectorsForRoute = (outerConfig?.sector || []).reduce((acc, s) => {
    if (s.routeCode === routeCode) acc[s.sector] = true;
    else if (s.routeCode) acc[s.sector] = false;
    return acc;
  }, {});

  const isForRoute = (sector) => {
    if (sectorsForRoute[sector] === undefined) {
      return outerConfig?.routeCode === routeCode;
    } else return sectorsForRoute[sector];
  };

  if (level === POSTCODE) {
    const postcodesForRoute = (outerConfig?.postcode || []).reduce((acc, p) => {
      if (p.routeCode === routeCode) acc[p.postcode] = true;
      else if (p.routeCode) acc[p.postcode] = false;
      return acc;
    }, {});
    toSector = postcodes[sector].every(
      (p) => p === postcode || !!postcodesForRoute[p]
    );
  }
  toOuter =
    (level === SECTOR || toSector) &&
    sectors.every((s) => s === sector || isForRoute(s));

  const preventSectorAllocation =
    type !== ADD_REDIRECT &&
    toSector &&
    outerConfig?.routeCode === routeEditCode;
  const outerAffectedRouteCode = toOuter && outerConfig?.routeCode;
  const sectorAffectedRouteCode = toSector && sectorConfig?.routeCode;

  const result = {};
  if (toOuter) result.toOuter = true;
  if (toSector) result.toSector = true;
  if (preventSectorAllocation) result.preventSectorAllocation = true;
  if (outerAffectedRouteCode)
    result.outerAffectedRouteCode = outerAffectedRouteCode;
  if (sectorAffectedRouteCode)
    result.sectorAffectedRouteCode = sectorAffectedRouteCode;
  return result;
};

const getActionRangeRemovals = ({
  outerConfig,
  routeCode,
  redirectionRouteCode,
  type,
  postcode,
  lookupTables,
}) => {
  const level = postcode.getLevel();
  if (level === POSTCODE) return [];

  if (type === ADD && level === SECTOR)
    return [{ routeCode, sectors: [postcode.toSector()] }];
  else if (type === ADD) {
    const sectors = groupSectors(lookupTables.SECTOR)[
      postcode.toOuter()
    ].filter(
      (sector) =>
        !outerConfig?.sector?.find(
          (s) => s.sector === sector && s.routeCode && s.routeCode !== routeCode
        )
    );
    return [{ routeCode, sectors }];
  } else if (type === ADD_REDIRECT) {
    return [
      {
        routeCode: redirectionRouteCode,
        sectors: [postcode.toSector()],
      },
    ];
  } else if (type === REMOVE) {
    const params = [];
    if (level === SECTOR && outerConfig?.routeCode) {
      params.push({
        routeCode: outerConfig?.routeCode,
        sectors: [postcode.toSector()],
      });
    }
    if (level === OUTER) {
      const sectors = groupSectors(lookupTables.SECTOR)[
        postcode.toOuter()
      ].filter(
        (sector) =>
          !outerConfig?.sector?.find(
            (s) => s.sector === sector && isNonRedirectionRouteCode(s.routeCode)
          )
      );
      params.push({
        routeCode: redirectionRouteCode,
        sectors,
      });
    } else if (!outerConfig?.routeCode) {
      params.push({
        routeCode: redirectionRouteCode,
        sectors: [postcode.toSector()],
      });
    }
    return params;
  } else if (type === REMOVE_REDIRECT && outerConfig?.routeCode) {
    return [
      {
        routeCode: outerConfig.routeCode,
        sectors: [postcode.toSector()],
      },
    ];
  } else return [];
};

const getActionsUpsertVersion = ({
  routeEditCode,
  options,
  nestedRouteCodes,
  nestedLabelRouteCodes,
}) =>
  options.flatMap((option) => {
    const { type, level, sector, sectors, affectedRouteCode } = option;
    if (level === POSTCODE || ADD !== type || !nestedRouteCodes.length)
      return option;
    const sectorRange = level === OUTER ? sectors : [sector];

    return [
      {
        message: affectedRouteCode
          ? REPLACE_THIS_LEVEL(affectedRouteCode)
          : REPLACE_UNALLOCATED,
        labelRouteCodes: [routeEditCode, ...nestedLabelRouteCodes],
        ...option,
      },
      {
        message: REPLACE_ALL,
        labelRouteCodes: [routeEditCode],
        ...option,
        rangeRemovals: nestedRouteCodes.reduce(
          (acc, routeCode) => {
            acc.push({
              routeCode,
              sectors: sectorRange,
            });
            return acc;
          },
          [
            {
              routeCode: routeEditCode,
              sectors: sectorRange,
            },
          ]
        ),
      },
    ];
  });
